import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i)

const vectorIds = [1, 3, 4, 5, 6, 7, 8, 9, 10, ...range(13, 26), ...range(28, 35)]

const vectorSamples = vectorIds.map((n) => {
  const ext = n === 1 || n === 3 ? 'jpg' : 'jpeg'
  return {
    title: 'Vectorization',
    beforeImage: `/portfolio-snapshot/vector/d${n}b.${ext}`,
    afterImage: `/portfolio-snapshot/vector/d${n}a.${ext}`,
  }
})

const digitizationSamples = range(1, 15).map((n) => ({
  title: n === 5 ? 'LOGO Digitization' : 'Digitization',
  beforeImage: `/portfolio-snapshot/digitization/d${n}b.jpeg`,
  afterImage: `/portfolio-snapshot/digitization/d${n}a.jpeg`,
}))

const patchSamples = ['p11.jpg', 'p3.jpg', 'p4.jpg', 'before1.jpg', 'before4.jpg', 'before5.jpg'].map((f) => ({
  title: 'Patches',
  image: `/portfolio-snapshot/patches/${f}`,
}))

const portfolioCategories = [
  {
    title: 'Vectorization',
    description: 'Transform your raster images into clean, scalable vector art',
    samples: vectorSamples,
    gradient: 'from-orange-500 to-amber-500',
    solid: 'bg-orange-500',
    hover: 'hover:border-orange-300 hover:shadow-orange-100',
  },
  {
    title: 'Digitization',
    description: 'Professional embroidery digitization for all types of designs',
    samples: digitizationSamples,
    gradient: 'from-red-500 to-rose-500',
    solid: 'bg-red-500',
    hover: 'hover:border-red-300 hover:shadow-red-100',
  },
  {
    title: 'Patches',
    description: 'Unique patch designs for branding and identification',
    samples: patchSamples,
    gradient: 'from-amber-500 to-orange-500',
    solid: 'bg-amber-500',
    hover: 'hover:border-amber-300 hover:shadow-amber-100',
  },
]

const stats = [
  { number: '500+', label: 'Projects Completed' },
  { number: '98%', label: 'Client Satisfaction' },
  { number: '24hrs', label: 'Fastest Turnaround' },
  { number: '50+', label: 'Happy Clients' },
]

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.5, delay },
})

const PortfolioPage = () => {
  const [selectedImage, setSelectedImage] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('All')

  const openModal = (image, title) => {
    setSelectedImage({ image, title })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedImage(null)
  }

  useEffect(() => {
    if (!isModalOpen) return
    const onKey = (e) => e.key === 'Escape' && closeModal()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isModalOpen])

  const tabs = ['All', ...portfolioCategories.map((c) => c.title)]
  const visible = portfolioCategories.filter((c) => activeTab === 'All' || c.title === activeTab)

  // Before / After overlapping card (Vectorization & Digitization)
  const OverlappingCard = ({ sample, index, category }) => (
    <motion.div {...reveal((index % 3) * 0.08)} className="relative group">
      <div
        className={`rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${category.hover}`}
      >
        <div className="relative h-64">
          <div className="absolute left-0 top-0 w-3/5 h-full">
            <div
              className="h-full cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white/90 backdrop-blur-sm transition-all duration-300 hover:shadow-lg"
              onClick={() => openModal(sample.beforeImage, `${sample.title} - BEFORE`)}
            >
              <img
                src={sample.beforeImage}
                alt={`${sample.title} - Before`}
                className="w-full h-5/6 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="h-1/6 flex items-center justify-center bg-red-50">
                <span className="text-red-600 font-bold text-xs">BEFORE</span>
              </div>
            </div>
          </div>

          <div className="absolute right-0 top-4 w-3/5 h-full z-10">
            <div
              className="h-full cursor-pointer overflow-hidden rounded-2xl border-2 border-emerald-300/80 bg-white shadow-xl shadow-slate-300/50 transition-all duration-300 hover:shadow-2xl"
              onClick={() => openModal(sample.afterImage, `${sample.title} - AFTER`)}
            >
              <img
                src={sample.afterImage}
                alt={`${sample.title} - After`}
                className="w-full h-5/6 object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="h-1/6 flex items-center justify-center bg-emerald-50">
                <span className="text-emerald-600 font-bold text-xs">AFTER</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t border-dashed border-slate-300 pt-4 text-center">
          <h3 className="text-sm font-semibold text-slate-800">{sample.title}</h3>
        </div>
      </div>
    </motion.div>
  )

  // Patches card
  const RegularCard = ({ sample, index, category }) => (
    <motion.div
      {...reveal((index % 3) * 0.08)}
      className="group cursor-pointer"
      onClick={() => openModal(sample.image, sample.title)}
    >
      <div
        className={`relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${category.hover}`}
      >
        <div className={`absolute left-0 top-0 z-10 h-1.5 w-full ${category.solid}`} />
        <div className="h-56 w-full overflow-hidden pt-1.5">
          <img
            src={sample.image}
            alt={sample.title}
            className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="border-t border-dashed border-slate-300 p-4 bg-white">
          <h3 className="text-sm font-semibold text-slate-800 text-center leading-tight">{sample.title}</h3>
        </div>
      </div>
    </motion.div>
  )

  return (
    <div className="pt-20 min-h-screen bg-white text-slate-900 antialiased">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(55rem 26rem at 90% -10%, rgba(249,115,22,.45), transparent 60%), radial-gradient(40rem 22rem at -5% 110%, rgba(244,63,94,.25), transparent 60%)',
          }}
        />
        <svg
          aria-hidden="true"
          className="absolute bottom-8 left-0 w-full h-28 text-[#F97316]/50"
          viewBox="0 0 1200 112"
          preserveAspectRatio="none"
          fill="none"
        >
          <path
            d="M0 70 C 200 10, 400 120, 600 62 S 1000 10, 1200 70"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeDasharray="10 9"
            strokeLinecap="round"
          />
        </svg>
        <div className="relative max-w-6xl mx-auto px-6 py-28 md:py-40">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-6xl md:text-8xl font-extrabold tracking-tight leading-[0.98]"
          >
            Our{' '}
            <span className="bg-gradient-to-r from-orange-400 via-red-400 to-amber-400 bg-clip-text text-transparent">
              Portfolio
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 text-lg md:text-2xl text-slate-300 max-w-2xl leading-relaxed"
          >
            Take a look at our recent work and see the quality we deliver
          </motion.p>
        </div>
      </section>

      {/* Category tabs */}
      <div className="sticky top-20 z-30 border-b border-slate-200 bg-white/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex gap-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              aria-pressed={activeTab === tab}
              className={`shrink-0 rounded-full px-5 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-500/40 ${
                activeTab === tab
                  ? 'bg-slate-900 text-white'
                  : 'bg-stone-100 text-slate-600 hover:bg-orange-100 hover:text-orange-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Portfolio Categories */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div aria-hidden="true" className="pointer-events-none absolute -top-40 -right-40 h-[28rem] w-[28rem] rounded-full bg-orange-200/30 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -left-40 h-[28rem] w-[28rem] rounded-full bg-amber-200/30 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 space-y-28">
          {visible.map((category) => (
            <div key={category.title}>
              <motion.div {...reveal()} className="mb-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 items-end">
                <h2 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1]">
                  <span className={`bg-gradient-to-r ${category.gradient} bg-clip-text text-transparent`}>
                    {category.title}
                  </span>
                </h2>
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed">{category.description}</p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.samples.map((sample, sampleIndex) =>
                  category.title === 'Patches' ? (
                    <RegularCard key={sampleIndex} sample={sample} index={sampleIndex} category={category} />
                  ) : (
                    <OverlappingCard key={sampleIndex} sample={sample} index={sampleIndex} category={category} />
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Portfolio Stats */}
      <section className="relative overflow-hidden bg-slate-900 py-24 md:py-32 text-white">
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-orange-500/30 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-24 h-96 w-96 rounded-full bg-rose-500/20 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6">
          <motion.div {...reveal()} className="mb-14 md:mb-20 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-end">
            <h2 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.02]">
              Our{' '}
              <span className="bg-gradient-to-r from-orange-400 via-red-400 to-amber-400 bg-clip-text text-transparent">
                Achievements
              </span>
            </h2>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
              Numbers that speak for our quality and dedication
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                {...reveal(index * 0.1)}
                className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 pt-10 backdrop-blur"
              >
                <div className="absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r from-orange-500 to-amber-500" />
                <div className="text-5xl md:text-6xl font-extrabold tracking-tight bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent mb-3">
                  {stat.number}
                </div>
                <div className="border-t border-dashed border-white/20 pt-3 text-slate-300 font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-white px-6 py-24 md:py-32">
        <motion.div
          {...reveal()}
          className="relative max-w-6xl mx-auto overflow-hidden rounded-[2rem] bg-gradient-to-br from-orange-500 via-orange-500 to-red-500 px-8 py-16 md:py-24 text-center text-white shadow-2xl shadow-orange-200"
        >
          <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 h-72 w-72 rounded-full bg-white/20 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-3 rounded-[1.5rem] border-2 border-dashed border-white/40" />
          <div className="relative">
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-5">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg md:text-xl text-orange-50 mb-10 max-w-xl mx-auto leading-relaxed">
              Let's discuss your project and bring your vision to life with our expert team.
            </p>
            <a
              href="/contact"
              className="inline-block rounded-xl bg-slate-900 px-9 py-4 text-lg font-semibold text-white transition-colors hover:bg-white hover:text-slate-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/60"
            >
              Get Started Today
            </a>
          </div>
        </motion.div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
          >
            <div className="relative max-w-4xl max-h-[90vh] w-full h-full">
              <button
                onClick={closeModal}
                aria-label="Close image"
                className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-[#F97316] text-white rounded-full w-12 h-12 flex items-center justify-center transition-colors duration-200 backdrop-blur-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
              >
                <X className="w-6 h-6" />
              </button>

              <motion.div
                initial={{ scale: 0.94, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.25 }}
                className="w-full h-full flex items-center justify-center"
              >
                <img
                  src={selectedImage?.image}
                  alt={selectedImage?.title}
                  className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl ring-4 ring-white/10"
                  onClick={(e) => e.stopPropagation()}
                />
              </motion.div>

              {selectedImage?.title && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-6 py-3 text-white backdrop-blur-sm">
                  <h3 className="text-lg font-semibold text-center">{selectedImage.title}</h3>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default PortfolioPage